'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { useLocale, useTranslations } from 'next-intl';
import { ChangeEvent, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { Agreements } from './Agreements';

import { RadioGroup, Button } from '@components/Form';
import Loader from '@components/root/Loader';
import { submitIpressoAgreements } from '@lib/ipresso';
import { logout } from '@utils/api/client';
import { newslettersByCountries } from '@utils/newsletter';
import { useUserStore } from '@utils/user';

const schema = z.object({
  marketingAgreement: z.boolean().refine((value) => value === true),
  commercialOutside: z.boolean(),
  commercialInside: z.boolean(),
  newsletter: z.string().optional(),
});

type SchemaType = z.infer<typeof schema>;


export default function SignToNewsletter() {

  const locale = useLocale();
  const userStore = useUserStore();
  const [isAccepted, setIsAccepted] = useState<boolean>(false);
  const t = useTranslations();

  const email = userStore.user?.email;

  const defaultValues = {
    marketing: userStore?.newsletterData?.data?.agreementData?.marketing || false,
    commercialInside: userStore?.newsletterData?.data?.agreementData?.commercialInside || false,
    commercialOutside: userStore?.newsletterData?.data?.agreementData?.commercialOutside || false,
    newsletter: Object.keys(userStore?.newsletterData?.data?.categoryData || {}).shift() || undefined,
  }
  useEffect(() => {
    setValue('marketingAgreement', defaultValues.marketing);
    setValue('commercialInside', defaultValues.commercialInside);
    setValue('commercialOutside', defaultValues.commercialOutside);
    setValue('newsletter', defaultValues.newsletter);
  }, [defaultValues.marketing, defaultValues.commercialInside, defaultValues.commercialOutside, defaultValues.newsletter]);

  const {
    watch,
    handleSubmit,
    register,
    setValue,
    formState: { errors, isSubmitting }
  } = useForm<SchemaType>({
    defaultValues,
    resolver: zodResolver(schema),
  });

  const checkboxesWatched = (['marketingAgreement', 'commercialInside'] as const).map((name) => watch(name));
  const newsletter = watch('newsletter');
  const newsletterOptions = newslettersByCountries[locale]
    .map((newsletter) => {
      return {
        label: `${t(
          `newsletter.workingOccupation.${newsletter}`,
        )}`,
        value: newsletter,
      };
    });


  useEffect(() => {
    setIsAccepted(!!newsletter && checkboxesWatched.reduce((acc, curr) => acc && curr, true));
  }, [newsletter, ...checkboxesWatched]);

  const onSubmit = handleSubmit(async (data) => {
    const dataForIpresso = {
      "marketingAgreement": data.marketingAgreement,
      "commercialInside": data.commercialInside,
      "commercialOutside": data.commercialOutside || false,
      "locale": locale,
      "email": userStore.user?.email || '',
      "categoryData": {
        [data.newsletter!]: true
      },
    }

    await submitIpressoAgreements(dataForIpresso);
    await userStore.fetchUser();
  });

  const onSelectNewsletterChange = (e: ChangeEvent<HTMLInputElement>) => {
    setIsAccepted(!!e.target.value && checkboxesWatched.reduce((acc, curr) => acc && curr, true));
  };

  return (
    <form
      noValidate
      onSubmit={onSubmit}
      className="bg-white p-4 lg:p-8 dark:bg-dark-500 flex flex-col"
    >
      <div className="flex flex-col justify-center items-center">
        {(!isSubmitting) && (
          <>
            <h1 className="mb-4 lg:mb-6 text-2xl font-bold text-grenade text-center dark:text-white">
              {t('main.agreement.signupTitle')}
            </h1>
            <div className="mx-4 grid">
              <Agreements
                secondRequired={true}
                setValue={setValue}
                watch={watch}
                register={register}
                errors={errors}
              />
              <div>
                <h4 className="mb-2 text-lg">{t('newsletter.workingAs')}</h4>
              </div>
              <div>
                <RadioGroup
                  name="newsletter"
                  register={register}
                  error={!!errors.newsletter}
                  elements={newsletterOptions}
                  onChange={onSelectNewsletterChange}
                />
              </div>
            </div>

            <div className="flex flex-col justify-center items-center my-4 lg:my-8">
              <Button
                disabled={!isAccepted}
              >
                {t('main.agreement.subscribeToNewsletter')}
              </Button>
            </div>
            <div className="w-[360px] text-center">
              {t('main.agreement.loggedAs')}
            </div>
            <div className="w-[360px] mb-2 lg:mb-4 text-center font-bold">
              {email}
            </div>
            <div className="w-[360px] mb-2 lg:mb-4">
              <button
                className="block w-[360px] text-sm text-center underline dark:text-white hover:text-blue"
                onClick={async (e) => {
                  e.preventDefault();
                  await logout();
                }}
              >
                {t('main.navigation.logout')}
              </button>
            </div>
          </>
        )}
        {isSubmitting && (
          <Loader />
        )}
      </div>
    </form>
  );
}
